<template>
  <div class="chat">
    <BasePaneCard
      :isLoading="isLoading"
      heading="Conversa"
      unboxed
    >
      <template v-if="dataset.messages?.length">
        <div class="chat__details">
          <div class="chat__details__total-messages">
            {{ totalMessages }}
          </div>

          <div class="chat__details__attendance-time">
            {{ attendanceTime }}
          </div>
        </div>

        <div
          v-for="(message, index) in dataset.messages"
          :key="message.sid"
          class="chat__message"
        >
          <div
            v-if="getWhenGroup(message.date_created, index)"
            class="chat__message__when"
          >
            {{ getWhenGroup(message.date_created, index) | date('date-fancy') }}
          </div>

          <div
            class="chat__message__content"
            :class="{
              'chat__message__content--inbound': message.direction === 'inbound-api',
              'chat__message__content--outbound': message.direction === 'outbound-api'
            }"
          >
            <div class="chat__message__content__body">
              <template v-if="!message.content_type">
                {{ message.body }}
              </template>

              <template v-else>
                <video
                  v-if="message.content_type.includes('video')"
                  controls
                >
                  <source
                    type="video/mp4"
                    :src="message.media_url"
                  />
                </video>

                <audio
                  v-if="message.content_type.includes('audio')"
                  controls
                >
                  <source
                    type="audio/mp3"
                    :src="message.media_url"
                  />
                </audio>

                <img
                  v-if="message.content_type.includes('image')"
                  :src="message.media_url"
                />
              </template>
            </div>

            <div class="chat__message__content__when">
              <span :title="getDate(message.date_created)">
                {{ message.date_created | date('time') }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <BaseEmptyState v-else>
        <template v-if="dataset.error">
          <SvgIconRobot/>

          <h3>
            Ops! Algo deu errado...
          </h3>

          <p>
            Por favor tente novamente.
          </p>
        </template>

        <template v-else>
          <SvgIconPersonSearch/>

          <h3>
            Este atendimento ainda<br>
            não possui mensagens.
          </h3>
        </template>
      </BaseEmptyState>
    </BasePaneCard>
  </div>
</template>

<script>
import { dateFilter, numberFilter, capitalizeFilter } from '@/utils/filters'
import BasePaneCard from '@/components/base/BasePaneCard'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import { mapState } from 'vuex'
import axios from 'axios'
import dayjs from 'dayjs'
import durationPlugin from 'dayjs/plugin/duration'
import humanizeDuration from 'humanize-duration'

dayjs.extend(durationPlugin)

export default {
  name: 'PartialLeadHistoryChat',

  components: {
    BasePaneCard,
    BaseEmptyState
  },

  filters: {
    date: dateFilter,
    number: numberFilter
  },

  props: {
    leadId: Number
  },

  data () {
    return {
      isLoading: false,
      dataset: []
    }
  },

  computed: {
    ...mapState('base', ['mainApi']),

    totalMessages () {
      const { metrics } = this.dataset

      if (!metrics) {
        return ''
      }

      return `Total de mensagens trocadas: ${numberFilter(metrics.total_messages)}`
    },

    attendanceTime () {
      const { metrics } = this.dataset

      if (!metrics) {
        return ''
      }

      const [days, time] = metrics.date_difference.split(' ')
      const [hours, minutes, seconds] = time.split(':')

      const duration = dayjs.duration({
        seconds: Number(seconds),
        minutes: Number(minutes),
        hours: Number(hours),
        days: Number(days)
      })

      const milliseconds = duration.asMilliseconds()

      const humanized = humanizeDuration(milliseconds, {
        language: 'pt',
        conjunction: ' e ',
        serialComma: false,
      })

      return `${capitalizeFilter(humanized)} de atendimento`
    }
  },

  watch: {
    leadId () {
      this.fetchData()
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    async fetchData () {
      if (!this.leadId) {
        return
      }

      this.isLoading = true

      try {
        const url = `${this.mainApi}integration/app/get-message/${this.leadId}?metrics=true`
        const response = await axios.get(url)

        this.dataset = response.data
        this.dataset.messages = response.data.messages.reverse()
      } catch (error) {
        this.dataset = { error }

        throw error
      } finally {
        this.isLoading = false
      }
    },

    getWhenGroup (date, index) {
      const datePrevious = this.dataset[index - 1]?.date_created

      if (!datePrevious) {
        return date
      }

      return dayjs(date).isSame(dayjs(datePrevious), 'day')
        ? ''
        : date
    },

    getDate (date) {
      return dateFilter(date)
    }
  }
}
</script>

<style lang="scss" scoped>
.chat {
  height: 100%;

  /deep/ {
    .pane-card {
      height: 100%;

      &__body {
        overflow: {
          x: hidden;
          y: auto;
        }
      }
    }

    .heading {
      &--small {
        font: {
          weight: 600;
          size: 14px;
        }
      }
    }
  }

  &__details {
    padding-bottom: 10px;
    color: nth($colors-custom, 15);
    font-size: 12px;
    line-height: 140%;
  }

  &__message {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-right: 20px;

    &:not(:first-child) {
      margin-top: 15px;
    }

    &__when {
      padding: 2px 10px;
      margin-bottom: 15px;
      border: 1px solid rgba(nth($colors-custom, 1), 0.2);
      border-radius: 2em;
      background: #fff;
      font-size: 10px;
    }

    &__content {
      min-width: 70px;
      max-width: 85%;
      padding: 10px;
      border: 1px solid;
      border-radius: 20px;

      &--inbound {
        align-self: flex-start;
        border-color: rgba(nth($colors-custom, 1), 0.2);
        border-bottom-left-radius: 0;
        background: #fff;
        color: nth($colors-custom, 1);
      }

      &--outbound {
        align-self: flex-end;
        border-color: nth($colors-custom, 1);
        border-bottom-right-radius: 0;
        background: nth($colors-custom, 1);
        color: #fff;
      }

      &__body {
        font-size: 11px;
        line-height: 140%;

        video,
        audio,
        img {
          display: block;
          width: 100%;
          max-width: 300px;
          margin: 0 auto;
        }
      }

      &__when {
        margin-top: 5px;
        opacity: 0.5;
        font-size: 10px;
        line-height: 100%;
        text-align: right;
      }
    }
  }
}
</style>
